import { Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { LayerService } from 'src/app/core/services/open-api/layer.service';
import { Layer } from 'src/app/core/models/layer';
import { combineLatest, startWith } from 'rxjs';
import { Column, ReportSettings } from '@/core/models/report-settings.interface';
import { AngularTranslations } from '@/core/types/angular-primeng-api';
import { ActivityDetailVmKeys } from '@/core/models/activity';

export type ColumnKey =
    | 'activities_user'
    | 'activities_note'
    | 'activities_date'
    | 'activities_time'
    | 'activities_duration'
    | 'activities_billed'
    | 'layer_1'
    | 'layer_2'
    | 'layer_3';

@Injectable({
    providedIn: 'root',
})
export class ColumnMappingService {
    private columnKeyMap: Map<ColumnKey, number> = new Map<ColumnKey, number>();
    private columns: string[] = [];

    constructor(
        private translateService: TranslateService,
        private layerService: LayerService,
    ) {
        const langChange$ = this.translateService.onLangChange.pipe(startWith(null as LangChangeEvent | null));
        combineLatest<[Layer[], LangChangeEvent | null]>([this.layerService.layers$, langChange$]).subscribe(
            ([layers, _langEvent]: [Layer[], LangChangeEvent | null]) => {
                // update columnMap based on layers or language change
                this.updateColMapBasedOnLayersUpdate(layers);
            },
        );
    }

    private resetColumns(): void {
        this.columns = [];
        this.columnKeyMap = new Map<ColumnKey, number>();
    }

    private updateColMapBasedOnLayersUpdate(layers: Layer[]): void {
        this.translateService
            .get([
                'activities_user',
                'activities_note',
                'activities_date',
                'activities_time',
                'activities_duration',
                'activities_billed',
            ])
            .subscribe((tr: AngularTranslations) => {
                this.resetColumns();
                this.addColumn('activities_user', tr['activities_user']);
                layers.forEach((layer: Layer) => {
                    if (layer.name !== undefined && layer.index !== undefined) {
                        const key = ('layer_' + layer.index) as ColumnKey;
                        this.addColumn(key, layer.name);
                    } else {
                        console.error(
                            'UNEXPECTED ERROR: ColumnMappingService - Layer name or index is undefined:',
                            layer,
                        );
                    }
                });
                this.addColumn('activities_note', tr['activities_note']);
                this.addColumn('activities_date', tr['activities_date']);
                this.addColumn('activities_time', tr['activities_time']);
                this.addColumn('activities_duration', tr['activities_duration']);
                this.addColumn('activities_billed', tr['activities_billed']);
            });
    }

    private addColumn(key: ColumnKey, translation: string): void {
        const index = this.columns.push(translation) - 1;
        this.columnKeyMap.set(key, index);
    }

    public isOnlyVisibleForAdminsAndManager(key: ColumnKey): boolean {
        return key === 'activities_billed' || key === 'activities_user';
    }

    /**
     * Returns the column index for the given key.
     * If the key is a layer index key, it will return the column index even if the layer is not visible.
     * @param key the translation key or layer_${index}
     * @returns the column index for the given key
     */
    public getColumnIndexByKey(key: ColumnKey): number {
        return this.columnKeyMap.get(key)!;
    }

    public getColumnKeyByIndex(index: number): ColumnKey {
        for (const [key, value] of this.columnKeyMap) {
            if (value === index) {
                return key;
            }
        }
        throw new Error(`ColumnMappingService.getColumnKeyByIndex(${index}): Column index not found in columnKeyMap!`);
    }

    public getColumnName(columnIndex: number): string {
        return this.columns[columnIndex];
    }

    public getColumnNameByKey(key: ColumnKey): string {
        return this.getColumnName(this.getColumnIndexByKey(key));
    }

    /**
     * @deprecated
     * TODO remove this method. It only exists for backwards compability to legacy excel code
     * @param translation
     */
    public getKeyForTranslation(translation: string): ColumnKey {
        const index = this.columns.findIndex((column) => {
            return column === translation;
        });
        if (index === -1) {
            const msg = `ColumnMappingService.getKeyForTranslation(): Translation not found for "${translation}"`;
            console.error(msg, this.columns);
            throw new Error(msg);
        }
        for (const [columnKey, mapIndex] of this.columnKeyMap.entries()) {
            if (mapIndex === index) {
                return columnKey;
            }
        }
        throw new Error('ColumnMappingService.getKeyForTranslation(): Column did not exist in map!');
    }

    /**
     * A function to check if a given column name is in the tableColumns.
     * @param key the key of the column to check
     * @param isPro true if the user is a pro user
     * @param isAdminOrManager true if the user is an admin or manager
     * @param reportSettings the report settings
     * @param layers optional parameter, when passed in, the layers visibility is also considered for determining if the column is visible
     * @returns true if the column ID is in the tableColumns. false otherwise.
     */
    public isColumnVisible(
        key: ColumnKey,
        isPro: boolean,
        isAdminOrManager: boolean,
        reportSettings: ReportSettings,
        layers?: Layer[],
    ): boolean {
        /**
         * When a user loses their PRO membership on the time entries
         * page, all columns are shown because filtering and hiding
         * specific columns is a feature exclusive to PRO members.
         * If the user previously filtered out columns while they
         * had PRO, this preference is disabled after the membership downgrade, so all columns become visible.
         * Furthermore, FREE users should always have access to all columns.
         */

        if (!isPro) {
            return layers ? ColumnMappingHelper.isVisibleLayerColumnOrTrue(key, layers) : true;
        }

        if (!isAdminOrManager && this.isOnlyVisibleForAdminsAndManager(key)) {
            return false;
        }

        const columnId = this.getColumnIndexByKey(key);

        // if pro user then check visibility based on the report settings
        const column = reportSettings.tableColumns.find((column) => column.id === columnId);
        const visible = column ? column.visible : true;

        if (layers) {
            const layerVisible = ColumnMappingHelper.isVisibleLayerColumnOrTrue(key, layers);
            return visible && layerVisible;
        } else {
            return visible;
        }
    }
}

// TODO the helper class should be removed - This whole topic is way too complex even after refactor...
export class ColumnMappingHelper {
    /**
     * Returns true if the column is a layer column and the layer is visible.
     * If the column is not a layer column, it always returns true.
     */
    public static isVisibleLayerColumnOrTrue(columnKey: ColumnKey, layers: Layer[]): boolean {
        if (columnKey.startsWith('layer_')) {
            const layerIndex = parseInt(columnKey.substring(6));
            return layers[layerIndex - 1]?.visible ?? false;
        }
        return true;
    }

    public static getActivityKeyByColumnKey(columnKey: ColumnKey): ActivityDetailVmKeys | undefined {
        switch (columnKey) {
            case 'activities_user':
                return 'userName';
            case 'activities_note':
                return 'note';
            case 'activities_date':
                return 'date';
            case 'activities_time':
                return 'time';
            // Note: The following commented fields (legacy code) did not exist on the column mapping service (dead code)
            // case 'activities_start_date':
            //    return 'startDate';
            // case 'activities_end_date':
            //    return 'endDate';
            // case 'activities_start_time':
            //    return 'startTime';
            // case 'activities_end_time':
            //    return 'endTime';
            case 'activities_duration':
                return 'duration';
            case 'activities_billed':
                return 'billed';
            // case for layers
            case 'layer_1':
                return 'layer1';
            case 'layer_2':
                return 'layer2';
            case 'layer_3':
                return 'layer3';
            default:
                return undefined;
        }
    }

    public static getColumnExportData(
        columnMappingService: ColumnMappingService,
        columns: Column[],
        layers: Layer[],
    ): ColumnExportData[] {
        const columnData: ColumnExportData[] = [];
        columns.forEach((column: Column) => {
            const columnKey = columnMappingService.getColumnKeyByIndex(column.id);
            const isVisibleLayerColumnOrTrue = this.isVisibleLayerColumnOrTrue(columnKey, layers);
            const isVisible = column.visible && isVisibleLayerColumnOrTrue;
            columnData.push({
                columnKey: columnKey,
                columnIndex: column.id,
                columnName: columnMappingService.getColumnName(column.id),
                columnVisible: isVisible,
                columnActivityKey: this.getActivityKeyByColumnKey(columnKey),
            });
        });
        return columnData;
    }
}

export type ColumnExportData = {
    columnKey: ColumnKey;
    columnIndex: number;
    columnName: string;
    columnVisible: boolean;
    columnActivityKey: ActivityDetailVmKeys | undefined;
};

export class ColumnVisibilityCache {
    // dependency copies:
    private isPro?: boolean;
    private isAdminOrManager?: boolean;
    private reportSettings?: ReportSettings;
    private columnVisibleCache = new Map<ColumnKey, boolean>();

    constructor(private readonly columnMappingService: ColumnMappingService) {}

    /**
     * Cached version of ColumnMappingService.isColumnVisible()
     * @see ColumnMappingService.isColumnVisible()
     */
    public isColumnVisible(
        key: ColumnKey,
        isPro: boolean,
        isAdminOrManager: boolean,
        reportSettings: ReportSettings,
    ): boolean {
        if (
            this.isPro !== isPro ||
            this.isAdminOrManager !== isAdminOrManager ||
            this.reportSettings !== reportSettings
        ) {
            // invalidate cache when a dependency changes
            this.columnVisibleCache.clear();
            this.isPro = isPro;
            this.isAdminOrManager = isAdminOrManager;
            this.reportSettings = reportSettings;
        }
        const cachedVisible = this.columnVisibleCache.get(key);
        if (cachedVisible !== undefined) {
            return cachedVisible;
        }
        const visible = this.columnMappingService.isColumnVisible(key, isPro, isAdminOrManager, reportSettings);
        this.columnVisibleCache.set(key, visible);
        return visible;
    }
}
